import { BaseApiClient } from './base-client'
import { API_ROUTES } from '@super/shared-types'
import type {
  VoicePipelineDashboardResponse,
  VoicePipelineSessionsListResponse,
  VoicePipelineMetrics,
} from '@super/shared-types'
import { withErrorHandling } from '../utils'

export interface DirectionCallMetrics {
  handledByAI: number
  totalAiTalkTime: number
  averageAiTalkTime: number
  // Optional so an admin build ahead of the API still renders.
  callsWithAiTalkTime?: number
  resolvedCalls: number
  notResolvedCalls: number
  totalProcessedCalls: number
  notResolvedPercentage: number
  earliestDataDate?: string | null
}

export interface CallReportResponse extends DirectionCallMetrics {
  total: DirectionCallMetrics
  outbound: DirectionCallMetrics
  // Optional so an admin build ahead of the API still renders.
  callsTransferred?: number
}

export interface SmsReportResponse {
  handledByAI: number
  resolvedSMS: number
  notResolvedSMS: number
  resolvedPercentage: number
  notResolvedPercentage: number
  totalMessagesSent: number
  averageTurnsPerSession: number
  openSessionsCount: number
  startDate?: string
  endDate?: string
}

export interface ActivityDataPoint {
  date: string
  callsHandled: number
  callsHandledOutbound: number
  callsHandledTotal: number
  callsResolved: number
  smsHandled: number
  smsResolved: number
}

export interface ActivityReportResponse {
  dataPoints: ActivityDataPoint[]
  startDate?: string
  endDate?: string
}

export interface ActionMetricsSummary {
  totalActions: number
  successfulActions: number
  failedActions: number
  successRate: number
  totalTimeSaved: number
}

export interface ActionMetricsByType {
  PHONE_CALL: number
  EMAIL: number
  SMS: number
  PUSH_INTEGRATION: number
}

export interface ActionMetricsByWorkflow {
  workflowName: string
  count: number
}

export interface ActionMetricsResponse {
  summary: ActionMetricsSummary
  byType: ActionMetricsByType
  byWorkflow: ActionMetricsByWorkflow[]
  startDate?: string
  endDate?: string
}

export interface IndividualCallRecord {
  date: string
  direction: string
  from: string
  to: string
  aiLedDuration: number
  assistantStatus: string
  isResolved: boolean | null
  hangup: boolean
  sentiment: string
  transcript: string
  handoffRule: string
}

export interface IndividualCallReportResponse {
  records: IndividualCallRecord[]
}

export interface ReportQueryParams {
  startDate?: string
  endDate?: string
  companyId?: string
}

// Re-export shared types so consumers can import from this module
export type {
  VoicePipelineSummary,
  VoicePipelineSttModelComparisonItem,
  VoicePipelineLlmModelComparisonItem,
  VoicePipelineTtsModelComparisonItem,
  VoicePipelineSessionTrendItem,
  VoicePipelineDashboardResponse,
  VoicePipelineSessionRowItem,
  VoicePipelineSessionsListResponse,
  VoicePipelineMetrics,
} from '@super/shared-types'

export interface VoicePipelineSessionsQueryParams {
  startDate?: string
  endDate?: string
  companyId?: string
  language?: string
  page?: number
  pageSize?: number
  sortBy?: string
  sortDir?: 'asc' | 'desc'
}

export class ReportingApiClient extends BaseApiClient {
  /**
   * Get call reporting metrics
   * @param params - Query parameters for date range
   * @returns CallReportResponse | null
   */
  async getCallReport(
    params?: ReportQueryParams,
  ): Promise<CallReportResponse | null> {
    return withErrorHandling(
      async () => {
        const queryString = this.buildQueryString(params || {})
        const response = await this.request<{ data: CallReportResponse }>(
          `${API_ROUTES.REPORTING.CALL}${queryString}`,
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching call report',
      },
    )
  }

  /**
   * Get SMS reporting metrics
   * @param params - Query parameters for date range
   * @returns SmsReportResponse | null
   */
  async getSmsReport(
    params?: ReportQueryParams,
  ): Promise<SmsReportResponse | null> {
    return withErrorHandling(
      async () => {
        const queryString = this.buildQueryString(params || {})
        const response = await this.request<{ data: SmsReportResponse }>(
          `${API_ROUTES.REPORTING.SMS}${queryString}`,
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching SMS report',
      },
    )
  }

  /**
   * Get activity trends with daily breakdowns
   * @param params - Query parameters for date range
   * @returns ActivityReportResponse | null
   */
  async getActivityReport(
    params?: ReportQueryParams,
  ): Promise<ActivityReportResponse | null> {
    return withErrorHandling(
      async () => {
        const queryString = this.buildQueryString(params || {})
        const response = await this.request<{ data: ActivityReportResponse }>(
          `${API_ROUTES.REPORTING.ACTIVITY}${queryString}`,
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching activity report',
      },
    )
  }

  /**
   * [Admin] Get call reporting metrics for a specific company
   * @param params - Query parameters including companyId
   * @returns CallReportResponse | null
   */
  async getAdminCallReport(
    params: ReportQueryParams & { companyId: string },
  ): Promise<CallReportResponse | null> {
    return withErrorHandling(
      async () => {
        const queryString = this.buildQueryString(params)
        const response = await this.request<{ data: CallReportResponse }>(
          `${API_ROUTES.ADMIN.REPORTING.CALL}${queryString}`,
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching admin call report',
      },
    )
  }

  /**
   * [Admin] Get SMS reporting metrics for a specific company
   * @param params - Query parameters including companyId
   * @returns SmsReportResponse | null
   */
  async getAdminSmsReport(
    params: ReportQueryParams & { companyId: string },
  ): Promise<SmsReportResponse | null> {
    return withErrorHandling(
      async () => {
        const queryString = this.buildQueryString(params)
        const response = await this.request<{ data: SmsReportResponse }>(
          `${API_ROUTES.ADMIN.REPORTING.SMS}${queryString}`,
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching admin SMS report',
      },
    )
  }

  /**
   * [Admin] Get activity trends with daily breakdowns for a specific company
   * @param params - Query parameters including companyId
   * @returns ActivityReportResponse | null
   */
  async getAdminActivityReport(
    params: ReportQueryParams & { companyId: string },
  ): Promise<ActivityReportResponse | null> {
    return withErrorHandling(
      async () => {
        const queryString = this.buildQueryString(params)
        const response = await this.request<{ data: ActivityReportResponse }>(
          `${API_ROUTES.ADMIN.REPORTING.ACTIVITY}${queryString}`,
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching admin activity report',
      },
    )
  }

  /**
   * Get individual call metrics for CSV export
   * @param params - Query parameters for date range
   * @returns IndividualCallReportResponse | null
   */
  async getIndividualCallReport(
    params?: ReportQueryParams,
  ): Promise<IndividualCallReportResponse | null> {
    return withErrorHandling(
      async () => {
        const queryString = this.buildQueryString(params || {})
        const response = await this.request<{
          data: IndividualCallReportResponse
        }>(`${API_ROUTES.REPORTING.CALL_INDIVIDUAL}${queryString}`)
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching individual call report',
      },
    )
  }

  /**
   * [Admin] Get individual call metrics for a specific company (CSV export)
   * @param params - Query parameters including companyId
   * @returns IndividualCallReportResponse | null
   */
  async getAdminIndividualCallReport(
    params: ReportQueryParams & { companyId: string },
  ): Promise<IndividualCallReportResponse | null> {
    return withErrorHandling(
      async () => {
        const queryString = this.buildQueryString(params)
        const response = await this.request<{
          data: IndividualCallReportResponse
        }>(`${API_ROUTES.ADMIN.REPORTING.CALL_INDIVIDUAL}${queryString}`)
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching admin individual call report',
      },
    )
  }

  /**
   * Get action metrics
   * @param params - Query parameters for date range
   * @returns ActionMetricsResponse | null
   */
  async getActionMetrics(
    params?: ReportQueryParams,
  ): Promise<ActionMetricsResponse | null> {
    return withErrorHandling(
      async () => {
        const queryString = this.buildQueryString(params || {})
        const response = await this.request<{ data: ActionMetricsResponse }>(
          `${API_ROUTES.REPORTING.ACTIONS}${queryString}`,
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching action metrics',
      },
    )
  }

  /**
   * [Admin] Get action metrics for a specific company
   * @param params - Query parameters including companyId
   * @returns ActionMetricsResponse | null
   */
  async getAdminActionMetrics(
    params: ReportQueryParams & { companyId: string },
  ): Promise<ActionMetricsResponse | null> {
    return withErrorHandling(
      async () => {
        const queryString = this.buildQueryString(params)
        const response = await this.request<{ data: ActionMetricsResponse }>(
          `${API_ROUTES.ADMIN.REPORTING.ACTIONS}${queryString}`,
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching admin action metrics',
      },
    )
  }

  /**
   * [Admin] Get voice pipeline performance dashboard
   * @param params - Query parameters (companyId optional for platform-wide view)
   * @returns VoicePipelineDashboardResponse | null
   */
  async getVoicePipelineDashboard(
    params?: ReportQueryParams,
  ): Promise<VoicePipelineDashboardResponse | null> {
    return withErrorHandling(
      async () => {
        const queryString = this.buildQueryString(params || {})
        const response = await this.request<{
          data: VoicePipelineDashboardResponse
        }>(`${API_ROUTES.ADMIN.REPORTING.PERFORMANCE}${queryString}`)
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching voice pipeline dashboard',
      },
    )
  }

  async getVoicePipelineSessions(
    params?: VoicePipelineSessionsQueryParams,
  ): Promise<VoicePipelineSessionsListResponse | null> {
    return withErrorHandling(
      async () => {
        const queryString = this.buildQueryString(params || {})
        const response = await this.request<{
          data: VoicePipelineSessionsListResponse
        }>(`${API_ROUTES.ADMIN.REPORTING.PERFORMANCE_SESSIONS}${queryString}`)
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching voice pipeline sessions',
      },
    )
  }

  async getVoicePipelineSessionTurns(
    conferenceId: string,
  ): Promise<VoicePipelineMetrics[] | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<{
          data: { turns: VoicePipelineMetrics[] }
        }>(
          API_ROUTES.ADMIN.REPORTING.PERFORMANCE_SESSION_TURNS.replace(
            ':conferenceId',
            conferenceId,
          ),
        )
        return response.data?.turns ?? null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching session turns',
      },
    )
  }
}

// Export a singleton instance
export const reportingApiClient = new ReportingApiClient()
